import React, { useState } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import { materialsymbolsdownload, wg_report_logo } from "images";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import {
  registerSupplierDownloadApi,
  getStateApi,
} from "services/supplierDiversity/supplierDiversity.thunks";
import { ERR_MSG } from "constants/labels";
import { alertActions } from "_store";

const ReportsDownload = ({ expiringInData, supplierData, type, hasFilter }) => {
  const header = "Supplier Development";
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [downloadData, setDownloadData] = useState(null);
  const [stateData, setStateData] = useState([]);

  const handleClose = () => setAnchorEl(null);
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);

  const newSupplierPayload = {
    RegisteredFrom: startOfYear,
    RegisteredTo: today,
    CompanyName: null,
    StateId: null,
    CategoryId: null,
    ExpiringIn: null,
  };
  // Define the async fetch function first
  const fetchDownloadData = async () => {
    const typeData = type === "newSuppliers" ? newSupplierPayload : hasFilter;
    try {
      const res = await dispatch(
        registerSupplierDownloadApi(typeData)
      ).unwrap();
      setDownloadData(res?.Data || []);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const fetchState = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getStateApi()).unwrap();
      if (res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message,
            header: header,
          })
        );
        return;
      }
      const sdState = res?.Data?.map((data) => {
        return { label: data?.StateDescription, value: data?.StateId };
      });
      setStateData(sdState);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const getStateLabel = (stateNum) => {
    const stateLabel = stateData.find(
      (item) => item.value === Number(stateNum)
    )?.label;

    return stateLabel;
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget); // open menu immediately
    fetchDownloadData(); // async call
    fetchState();
  };

  const isExpiring = type === "expiringIN";
  const supplierDiversityData = isExpiring ? expiringInData : downloadData;
  const convertToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Registered Vendors");

    // REGISTERED / NORMAL HEADER
    const headersRegistered = [
      "Date of Registration",
      "Company Name",
      "Company Website",
      "Business Category",
      "Classification",
      "Contact Person",
      "Title",
      "Email",
      "Phone Number",
      "Mobile Number",
      "Address",
      "State",
      "Zip Code",
      "Date Last Updated",
    ];

    // EXPIRING SUPPLIERS HEADER
    const headersExpiring = [
      "Company Name",
      "Email Address",
      "Date of Registration",
      "Expiration Date",
      "Days until Expiration",
    ];

    // Determine current mode

    const headers = isExpiring ? headersExpiring : headersRegistered;

    // LOAD LOGO
    const response = await fetch(wg_report_logo);
    const imageBlob = await response.blob();
    const base64 = await convertToBase64(imageBlob);

    const imageId = workbook.addImage({
      base64: base64,
      extension: "png",
    });

    // TITLE + LOGO
    const titleReport = isExpiring
      ? "Certificates Expiring in 30-60-90 Days"
      : "Registered Vendors Report";
    worksheet.addRow([titleReport]);
    const titleRow = worksheet.getRow(1);
    titleRow.height = 50;
    titleRow.getCell(1).font = { bold: true, size: 16 };
    titleRow.getCell(1).alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    worksheet.mergeCells("A1:F1");

    worksheet.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 180, height: 50 },
    });

    worksheet.addRow([]);
    worksheet.addRow([`Date Published: ${dayjs().format("MMMM DD, YYYY")}`]);
    worksheet.addRow([]);

    // HEADER ROW
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF0F3557" },
      };
      cell.alignment = { horizontal: "center" };
    });

    // BODY ROWS — CONDITIONAL
    supplierDiversityData?.forEach((item) => {
      if (isExpiring) {
        // EXPIRING SUPPLIERS
        worksheet.addRow([
          item.CompanyName ?? " ",
          item.EmailAddress ?? " ",
          dayjs(item.RegistrationDate).format("MMMM DD, YYYY"),
          item.ExpirationDate
            ? dayjs(item.ExpirationDate).format("MMMM DD, YYYY")
            : " ",
          Number(item.DaysUntilExpiration) ?? "0",
        ]);
      } else {
        worksheet.addRow([
          dayjs(item.RegistrationDate).format("MMMM DD, YYYY"),
          item.CompanyName ?? " ",
          item.CompanyWebsite ?? " ",
          item.BusinessCategory ?? " ",
          item.Classification ?? " ",
          item.ContactPerson ?? " ",
          item.Title ?? " ",
          item.Email ?? " ",
          item.PhoneNumber ?? " ",
          item.MobileNumber ?? " ",
          item.Address ?? " ",
          getStateLabel(item.State) ?? " ",
          item.ZipCode ?? " ",
          item.UpdatedDate
            ? dayjs(item.UpdatedDate).format("MMMM DD, YYYY")
            : " ",
        ]);
      }
    });

    // AUTO-FIT COLUMNS
    worksheet.columns.forEach((column) => {
      let max = 12;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const length = cell.value ? cell.value.toString().length : 0;
        if (length > max) max = length;
        cell.alignment = { horizontal: "center" };
      });
      column.width = max + 2;
    });

    // FILENAME
    const filename = isExpiring
      ? "Certificates_Expiring_in_30_60_90_Days.xlsx"
      : "Registered_Vendors_Report.xlsx";

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      filename
    );
  };

  const downloadPDF = () => {
    const doc = new jsPDF({
      orientation: !isExpiring ? "landscape" : "portrait", // conditional orientation
      unit: "pt", // points
      format: isExpiring ? "a4" : "legal",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 50;
    const logoHeight = 18;
    const titleReport = isExpiring
      ? "Certificates Expiring in 30-60-90 Days"
      : "Registered Vendors Report";
    doc.addImage(wg_report_logo, "PNG", 15, 10, logoWidth, logoHeight);

    doc.setFontSize(16);
    doc.text(titleReport, pageWidth / 2, 40, { align: "center" });

    doc.setFontSize(12);
    doc.text(`Date Published: ${dayjs().format("MMMM DD, YYYY")}`, 40, 70);

    // HEADERS
    const headersRegistered = [
      [
        "Date of Registration",
        "Company Name",
        "Company Website",
        "Business Category",
        "Classification",
        "Contact Person",
        "Title",
        "Email",
        "Phone Number",
        "Mobile Number",
        "Address",
        "State",
        "Zip Code",
        "Date Last Updated",
      ],
    ];

    const headersExpiring = [
      [
        "Company Name",
        "Email Address",
        "Date of Registration",
        "Expiration Date",
        "Days until Expiration",
      ],
    ];

    // CHOOSE HEADER BASED ON VIEW
    const isExpiringView = type === "expiringIN";
    const tableHeaders = isExpiringView ? headersExpiring : headersRegistered;

    // BODY ROWS
    const body = supplierDiversityData?.map((item) =>
      isExpiringView
        ? [
            item.CompanyName ?? " ",
            item.EmailAddress ?? " ",
            dayjs(item.RegistrationDate).format("MMMM DD, YYYY"),
            item.ExpirationDate
              ? dayjs(item.ExpirationDate).format("MMMM DD, YYYY")
              : " ",
            item.DaysUntilExpiration ?? " ",
          ]
        : [
            dayjs(item.RegistrationDate).format("MMMM DD, YYYY"),
            item.CompanyName ?? " ",
            item.CompanyWebsite ?? " ",
            item.BusinessCategory ?? " ",
            item.Classification ?? " ",
            item.ContactPerson ?? " ",
            item.Title ?? " ",
            item.Email ?? " ",
            item.PhoneNumber ?? " ",
            item.MobileNumber ?? " ",
            item.Address ?? " ",
            getStateLabel(item.State) ?? " ",
            item.ZipCode ?? " ",
            item.UpdatedDate
              ? dayjs(item.UpdatedDate).format("MMMM DD, YYYY")
              : "-",
          ]
    );

    doc.autoTable({
      startY: 80,
      head: tableHeaders,
      body: body,
      styles: { fontSize: 10, halign: "center" },
      headStyles: {
        fillColor: "#0F3557",
        textColor: "#FFFFFF",
        fontStyle: "bold",
      },
      columnStyles: {
        4: { cellWidth: 80 },
        7: { cellWidth: 120 },
      },
    });
    const title = isExpiring
      ? "Certificates_Expiring_in_30_60_90_Days.pdf"
      : "Registered_Vendors_Report.pdf";
    doc.save(title);
  };

  return (
    <>
      <Button
        variant="contained"
        className="Download"
        color="primary"
        onClick={handleClick}
      >
        <img src={materialsymbolsdownload} alt="Download" /> Download
      </Button>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={downloadPDF}>PDF</MenuItem>
        <MenuItem onClick={downloadExcel}>Excel</MenuItem>
      </Menu>
    </>
  );
};

export default ReportsDownload;
